import { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import type {
  ListConnectableToolkitsResult,
  ListedToolkit,
} from '@erikdakoda/connection/shared/toolkitCatalogTypes';
import { useFindManyComposioConnection } from '@erikdakoda/database/hooks/composio-connection';
import { useFindManyConnectionItem } from '@erikdakoda/database/hooks/connection-item';
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import IconFab from '@erikdakoda/ui/components/IconFab';
import ScrollBox from '@erikdakoda/ui/components/ScrollBox';
import Spinner from '@erikdakoda/ui/components/Spinner';
import { doFetch } from '@erikdakoda/ui/shared/doFetch';
import { useException } from '@erikdakoda/ui/hooks/useException';

import { ConnectionForCardSelect, type ConnectionForCard } from '@erikdakoda/connection/types';

import themeConfig from '@erikdakoda/site-template-ui/config';
import type { PinnedConnector } from '@erikdakoda/site-template-ui/types';

import type {
  MetricConnectionForCard,
  MetricConnectorDescriptor,
} from '../shared/metricConnectorTypes';
import { CONNECTION_CONNECTED_MESSAGE_TYPE } from '../shared/launchConnect';
import AddConnectionDialog from './AddConnectionDialog';
import ConnectionCard from './ConnectionCard';
import ToolkitLogo from './ToolkitLogo';

const ACTIVE_STATUSES = new Set(['ACTIVE']);
const PENDING_STATUSES = new Set(['INITIALIZING', 'INITIATED']);
const NEEDS_ATTENTION_STATUSES = new Set(['EXPIRED', 'FAILED', 'INACTIVE', 'REVOKED']);

const METRIC_ITEM_TYPES = new Set(['CloudConnection', 'OsConnection']);

const POLL_INTERVAL_MS = 5000;
const sectionSpacing = 14;
const innerSpacing = 4;

const StyledDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(innerSpacing),
  marginBottom: theme.spacing(innerSpacing),
}));

const METRIC_CONNECTION_ITEM_SELECT = {
  id: true,
  itemType: true,
  scope: true,
  status: true,
  statusReason: true,
  name: true,
  connectorKey: true,
  lastSyncedAt: true,
  toolAccessMode: true,
} as const;

/**
 * Maps a stored connectorKey to the pin slug (provider/toolkit prefix before `#`).
 * @param connectorKey Full ConnectionItem connectorKey.
 */
function pinKeyForConnectorKey(connectorKey: string): string {
  const hashIndex = connectorKey.indexOf('#');
  return hashIndex >= 0 ? connectorKey.slice(0, hashIndex) : connectorKey;
}

type ConnectionsPanelProps = {
  /** Restricts the panel to SERVICE-scope connections (admin/ops use only). Defaults to USER+SPACE. */
  readonly forService?: boolean;
  readonly className?: string;
  readonly pinnedConnectors?: readonly PinnedConnector[];
  readonly metricConnectors?: readonly MetricConnectorDescriptor[];
};

type LifecycleBucket = 'active' | 'needsAttention' | 'pending';

/**
 * User-facing panel listing Composio connections, grouped by lifecycle state, with an entry
 * point to connect a new toolkit. Lists USER rows owned by the signed-in user and SPACE rows
 * for the active space.
 * @param props Panel scope, styling, and connector configuration.
 * @param props.forService Restricts the panel to SERVICE-scope connections (admin/ops use only).
 * @param props.className Optional class name merged onto the root element.
 * @param props.pinnedConnectors Recommended connectors shown when not already connected.
 * @param props.metricConnectors OS/cloud metric connector descriptors wired by the host app.
 * @returns Grouped list of connections with header, empty state, and add-connection dialog.
 */
export default function ConnectionsPanel({
  forService = false,
  className,
  pinnedConnectors = themeConfig.config.pinnedConnectors,
  metricConnectors = [],
}: ConnectionsPanelProps) {
  'use memo';

  const theme = useTheme();
  const { t } = useTranslation('connection-ui');
  const { user } = useCurrentUser();
  const activeSpaceId = user?.activeSpaceId ?? null;
  const userId = user?.id ?? null;

  const [addConnectionOpen, setAddConnectionOpen] = useState(false);
  const [initialToolkitSlug, setInitialToolkitSlug] = useState<string | undefined>(undefined);
  const [reconnectTarget, setReconnectTarget] = useState<ConnectionForCard | null>(null);

  const where = forService
    ? { scope: 'SERVICE' as const, trashedAt: null }
    : {
        trashedAt: null,
        OR: [
          ...(userId ? [{ scope: 'USER' as const, ownerId: userId }] : []),
          ...(activeSpaceId ? [{ scope: 'SPACE' as const, spaceId: activeSpaceId }] : []),
        ],
      };

  const {
    data: connections,
    isLoading,
    refetch,
  } = useFindManyComposioConnection(
    {
      where,
      select: ConnectionForCardSelect,
      orderBy: [{ isDefault: 'desc' }, { createdAt: 'asc' }],
    },
    { enabled: forService || Boolean(userId) },
  );

  const metricWhere = forService
    ? { trashedAt: null, itemType: { in: [...METRIC_ITEM_TYPES] }, scope: 'SERVICE' as const }
    : {
        trashedAt: null,
        itemType: { in: [...METRIC_ITEM_TYPES] },
        OR: [
          ...(userId ? [{ scope: 'USER' as const, ownerId: userId }] : []),
          ...(activeSpaceId ? [{ scope: 'SPACE' as const, spaceId: activeSpaceId }] : []),
        ],
      };

  const { data: metricConnectionRows, refetch: refetchMetricConnections } =
    useFindManyConnectionItem(
      {
        where: metricWhere,
        select: METRIC_CONNECTION_ITEM_SELECT,
        orderBy: [{ createdAt: 'asc' }],
      },
      { enabled: !forService && Boolean(userId) },
    );

  const hasPending = (connections ?? []).some((connection) =>
    PENDING_STATUSES.has(connection.status),
  );

  useEffect(() => {
    if (!hasPending) return undefined;
    const intervalId = window.setInterval(() => void refetch(), POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [hasPending, refetch]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string } | null;
      if (data?.type === CONNECTION_CONNECTED_MESSAGE_TYPE) {
        void refetch();
        void refetchMetricConnections();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [refetch, refetchMetricConnections]);

  const connectedComposioSlugs = new Set<string>();
  const connectionCountByToolkitSlug: Record<string, number> = {};
  for (const connection of connections ?? []) {
    connectedComposioSlugs.add(connection.toolkitSlug);
    connectionCountByToolkitSlug[connection.toolkitSlug] =
      (connectionCountByToolkitSlug[connection.toolkitSlug] ?? 0) + 1;
  }

  const metricConnectionsByPinKey = new Map<string, MetricConnectionForCard[]>();
  for (const row of metricConnectionRows ?? []) {
    const hydrated = row as MetricConnectionForCard;
    if (!hydrated.connectorKey) {
      continue;
    }
    const pinKey = pinKeyForConnectorKey(hydrated.connectorKey);
    const list = metricConnectionsByPinKey.get(pinKey) ?? [];
    list.push(hydrated);
    metricConnectionsByPinKey.set(pinKey, list);
  }

  const refreshAll = (): void => {
    void refetch();
    void refetchMetricConnections();
  };
  const wrappedMetricConnectors: readonly MetricConnectorDescriptor[] = metricConnectors.map(
    (descriptor) => ({
      ...descriptor,
      onConnect: async () => {
        await descriptor.onConnect();
        refreshAll();
      },
      onDisconnect: async (connectionId: string) => {
        await descriptor.onDisconnect(connectionId);
        refreshAll();
      },
      onDelete: async (connectionId: string) => {
        await descriptor.onDelete(connectionId);
        refreshAll();
      },
      onResync: descriptor.onResync
        ? async (connectionId: string) => {
            await descriptor.onResync?.(connectionId);
            refreshAll();
          }
        : undefined,
    }),
  );

  const metricDescriptorByKey = new Map<string, MetricConnectorDescriptor>();
  for (const descriptor of wrappedMetricConnectors) {
    metricDescriptorByKey.set(descriptor.connectorKey, descriptor);
  }

  const groups: Record<LifecycleBucket, ConnectionForCard[]> = {
    active: [],
    needsAttention: [],
    pending: [],
  };

  const metricGroups: Record<LifecycleBucket, MetricConnectionForCard[]> = {
    active: [],
    needsAttention: [],
    pending: [],
  };

  for (const connection of connections ?? []) {
    if (ACTIVE_STATUSES.has(connection.status)) {
      groups.active.push(connection);
    } else if (NEEDS_ATTENTION_STATUSES.has(connection.status)) {
      groups.needsAttention.push(connection);
    } else {
      groups.pending.push(connection);
    }
  }

  const pinnedMetricKeys = new Set(
    pinnedConnectors
      .filter((pin) => pin.kind === 'os' || pin.kind === 'cloud')
      .map((p) => p.connectorKey),
  );

  for (const connectionsForPin of metricConnectionsByPinKey.values()) {
    for (const connection of connectionsForPin) {
      const pinKey = pinKeyForConnectorKey(connection.connectorKey);
      if (!pinnedMetricKeys.has(pinKey)) {
        continue;
      }
      if (ACTIVE_STATUSES.has(connection.status)) {
        metricGroups.active.push(connection);
      } else if (NEEDS_ATTENTION_STATUSES.has(connection.status)) {
        metricGroups.needsAttention.push(connection);
      } else {
        metricGroups.pending.push(connection);
      }
    }
  }

  const recommendedPins = pinnedConnectors.filter((pin) => {
    if (pin.kind === 'composio') {
      return !connectedComposioSlugs.has(pin.connectorKey);
    }
    if (pin.kind === 'os' || pin.kind === 'cloud') {
      const hasConnection = (metricConnectionsByPinKey.get(pin.connectorKey) ?? []).length > 0;
      return !hasConnection;
    }
    return true;
  });

  const handleReconnect = (connection: ConnectionForCard): void => {
    setReconnectTarget(connection);
  };

  const handleAddConnectionClose = (): void => {
    setAddConnectionOpen(false);
    setInitialToolkitSlug(undefined);
    setReconnectTarget(null);
  };

  const handleConnected = (): void => {
    void refetch();
    void refetchMetricConnections();
  };

  const openAddConnection = (toolkitSlug?: string): void => {
    setInitialToolkitSlug(toolkitSlug);
    setAddConnectionOpen(true);
  };

  // Recommended display order: OS → Cloud → Composio
  const osRecommendedPins = recommendedPins.filter((pin) => pin.kind === 'os');
  const cloudRecommendedPins = recommendedPins.filter((pin) => pin.kind === 'cloud');
  const composioRecommendedPins = recommendedPins.filter((pin) => pin.kind === 'composio');
  const metricRecommendedPins = [...osRecommendedPins, ...cloudRecommendedPins];

  const isEmpty =
    (connections ?? []).length === 0 &&
    metricGroups.active.length === 0 &&
    metricGroups.needsAttention.length === 0 &&
    metricGroups.pending.length === 0 &&
    recommendedPins.length === 0;

  return (
    <Box
      className={clsx('connections-panel', className)}
      sx={{ position: 'relative', height: '100%', minHeight: 0 }}
    >
      {isLoading ? (
        <Spinner className="connections-panel-loading" fullHeight />
      ) : (
        <ScrollBox scrollTrackOutside>
          <Typography variant="h6" sx={{ marginBottom: 8, pr: 18 }}>
            {t('connectionsDescription')}
          </Typography>
          {isEmpty ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                minHeight: 0,
                marginTop: -20,
              }}
            >
              <FaIconUvilo
                icon="puzzle"
                prefix="fat"
                size="lg"
                color="text.disabled"
                sx={{ width: '5em', height: '5em' }}
              />
              <Typography variant="h6" sx={{ mt: 2 }}>
                {t('emptyStateTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 8, mb: 4 }}>
                {t('emptyStateDescription')}
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                useFlexGap
                sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
              >
                {composioRecommendedPins.map((pin) => (
                  <Button
                    key={pin.connectorKey}
                    variant="outlined"
                    onClick={() => openAddConnection(pin.connectorKey)}
                    sx={{ gap: 2 }}
                    startIcon={
                      <ToolkitLogo toolkitSlug={pin.connectorKey} size={20} alt={pin.name} />
                    }
                  >
                    {pin.name}
                  </Button>
                ))}
                <Button
                  variant="outlined"
                  onClick={() => openAddConnection()}
                  sx={{ gap: 2 }}
                  startIcon={<FaIconUvilo icon="light-puzzle-piece-circle-plus" />}
                >
                  {t('moreConnections')}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              {groups.active.length > 0 || metricGroups.active.length > 0 ? (
                <ConnectionGroup
                  titleKey="groupActive"
                  connections={groups.active}
                  metricConnections={metricGroups.active}
                  metricDescriptorByKey={metricDescriptorByKey}
                  pinnedConnectors={pinnedConnectors}
                  connectionCountByToolkitSlug={connectionCountByToolkitSlug}
                  onChanged={handleConnected}
                  onReconnect={handleReconnect}
                />
              ) : null}
              {groups.needsAttention.length > 0 || metricGroups.needsAttention.length > 0 ? (
                <ConnectionGroup
                  titleKey="groupNeedsAttention"
                  connections={groups.needsAttention}
                  metricConnections={metricGroups.needsAttention}
                  metricDescriptorByKey={metricDescriptorByKey}
                  pinnedConnectors={pinnedConnectors}
                  connectionCountByToolkitSlug={connectionCountByToolkitSlug}
                  onChanged={handleConnected}
                  onReconnect={handleReconnect}
                />
              ) : null}
              {groups.pending.length > 0 || metricGroups.pending.length > 0 ? (
                <ConnectionGroup
                  titleKey="groupPending"
                  connections={groups.pending}
                  metricConnections={metricGroups.pending}
                  metricDescriptorByKey={metricDescriptorByKey}
                  pinnedConnectors={pinnedConnectors}
                  connectionCountByToolkitSlug={connectionCountByToolkitSlug}
                  onChanged={handleConnected}
                  onReconnect={handleReconnect}
                />
              ) : null}
              {metricRecommendedPins.length > 0 || composioRecommendedPins.length > 0 ? (
                <RecommendedConnectorsGroup
                  composioPins={composioRecommendedPins}
                  metricPins={metricRecommendedPins}
                  metricDescriptorByKey={metricDescriptorByKey}
                  onConnectComposio={openAddConnection}
                />
              ) : null}
            </Box>
          )}
        </ScrollBox>
      )}

      <IconFab
        icon="light-puzzle-piece-circle-plus"
        title={t('addConnection')}
        onClick={() => openAddConnection()}
        sx={{ position: 'absolute', top: theme.spacing(4), right: 0, zIndex: 1 }}
      />

      <AddConnectionDialog
        open={addConnectionOpen || Boolean(reconnectTarget)}
        onClose={handleAddConnectionClose}
        onConnected={handleConnected}
        forService={forService}
        initialToolkitSlug={reconnectTarget?.toolkitSlug ?? initialToolkitSlug}
        reconnectConnectionId={reconnectTarget?.id}
        initialScope={reconnectTarget?.scope === 'SPACE' ? 'SPACE' : 'USER'}
      />
    </Box>
  );
}

type ConnectionGroupProps = {
  readonly titleKey: string;
  readonly connections: readonly ConnectionForCard[];
  readonly metricConnections: readonly MetricConnectionForCard[];
  readonly metricDescriptorByKey: ReadonlyMap<string, MetricConnectorDescriptor>;
  readonly pinnedConnectors: readonly PinnedConnector[];
  readonly connectionCountByToolkitSlug: Readonly<Record<string, number>>;
  readonly onChanged: () => void;
  readonly onReconnect: (connection: ConnectionForCard) => void;
};

function ConnectionGroup({
  titleKey,
  connections,
  metricConnections,
  metricDescriptorByKey,
  pinnedConnectors,
  connectionCountByToolkitSlug,
  onChanged,
  onReconnect,
}: ConnectionGroupProps) {
  'use memo';

  const { t } = useTranslation('connection-ui');
  const pinByKey = new Map(pinnedConnectors.map((pin) => [pin.connectorKey, pin]));

  return (
    <Box className="connection-group" sx={{ marginBottom: sectionSpacing }}>
      <Typography variant="h3">{t(titleKey)}</Typography>
      <StyledDivider />
      <Grid container spacing={3}>
        {connections.map((connection) => (
          <Grid key={connection.id} size={{ xs: 6, sm: 4, md: 3 }} sx={{ display: 'flex' }}>
            <ConnectionCard
              connection={connection}
              onChanged={onChanged}
              onReconnect={onReconnect}
              showDefaultBadge={(connectionCountByToolkitSlug[connection.toolkitSlug] ?? 0) > 1}
            />
          </Grid>
        ))}
        {metricConnections.map((connection) => {
          const pinKey = pinKeyForConnectorKey(connection.connectorKey);
          const pin = pinByKey.get(pinKey);
          const descriptor = metricDescriptorByKey.get(pinKey);
          if (!pin || !descriptor) {
            return null;
          }
          return (
            <Grid key={connection.id} size={{ xs: 6, sm: 4, md: 3 }} sx={{ display: 'flex' }}>
              <ConnectionCard
                variant="metricConnector"
                pin={pin}
                connection={connection}
                authSchemes={descriptor.authSchemes}
                description={descriptor.description}
                connectEnabled={descriptor.connectEnabled ?? true}
                connectDisabledReason={descriptor.connectDisabledReason}
                onConnect={descriptor.onConnect}
                onDisconnect={descriptor.onDisconnect}
                onDelete={descriptor.onDelete}
                onResync={descriptor.onResync}
                resyncMenuTitle={descriptor.resyncMenuTitle}
                resyncMenuIcon={descriptor.resyncMenuIcon}
                renderConnectionMeta={descriptor.renderConnectionMeta}
              />
            </Grid>
          );
        })}
      </Grid>
      <StyledDivider />
    </Box>
  );
}

type RecommendedConnectorsGroupProps = {
  readonly composioPins: readonly PinnedConnector[];
  readonly metricPins: readonly PinnedConnector[];
  readonly metricDescriptorByKey: ReadonlyMap<string, MetricConnectorDescriptor>;
  readonly onConnectComposio: (toolkitSlug: string) => void;
};

function RecommendedConnectorsGroup({
  composioPins,
  metricPins,
  metricDescriptorByKey,
  onConnectComposio,
}: RecommendedConnectorsGroupProps) {
  'use memo';

  const { t } = useTranslation('connection-ui');
  const exception = useException();
  // keep: useException returns a new function identity each render; effects must not depend on it.
  const exceptionRef = useRef(exception);
  exceptionRef.current = exception;
  const [catalogBySlug, setCatalogBySlug] = useState<
    ReadonlyMap<string, ListConnectableToolkitsResult['items'][number]>
  >(() => new Map());
  const composioSlugs = composioPins.map((pin) => pin.connectorKey).join(',');

  useEffect(() => {
    if (composioSlugs.length === 0) {
      setCatalogBySlug(new Map());
      return;
    }
    let cancelled = false;
    void (async () => {
      const result = await doFetch<ListConnectableToolkitsResult>({
        url: '/api/connections/toolkits',
        method: 'GET',
        params: { slugs: composioSlugs },
        exception: exceptionRef.current,
      });
      if (cancelled) {
        return;
      }
      const next = new Map<string, ListConnectableToolkitsResult['items'][number]>();
      for (const toolkit of result?.items ?? []) {
        next.set(toolkit.slug, toolkit);
      }
      setCatalogBySlug(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [composioSlugs]);

  return (
    <Box className="recommended-connectors-group" sx={{ marginBottom: sectionSpacing }}>
      <Typography variant="h3">{t('groupRecommended')}</Typography>
      <StyledDivider />
      <Grid container spacing={3}>
        {metricPins.map((pin) => {
          const descriptor = metricDescriptorByKey.get(pin.connectorKey);
          if (!descriptor) {
            return null;
          }
          return (
            <Grid key={pin.connectorKey} size={{ xs: 6, sm: 4, md: 3 }} sx={{ display: 'flex' }}>
              <ConnectionCard
                variant="metricConnector"
                pin={pin}
                connection={null}
                authSchemes={descriptor.authSchemes}
                description={descriptor.description}
                connectEnabled={descriptor.connectEnabled ?? true}
                connectDisabledReason={descriptor.connectDisabledReason}
                onConnect={descriptor.onConnect}
                onDisconnect={descriptor.onDisconnect}
                onDelete={descriptor.onDelete}
                onResync={descriptor.onResync}
                resyncMenuTitle={descriptor.resyncMenuTitle}
                resyncMenuIcon={descriptor.resyncMenuIcon}
                renderConnectionMeta={descriptor.renderConnectionMeta}
              />
            </Grid>
          );
        })}
        {composioPins.map((pin) => {
          const catalog = catalogBySlug.get(pin.connectorKey);
          const toolkit: ListedToolkit = catalog ?? {
            slug: pin.connectorKey,
            name: pin.name,
            displayName: pin.name,
            description: '',
            authSchemes: [],
            composioManagedAuthSchemes: [],
            toolsCount: 0,
            triggersCount: 0,
            version: '',
            updatedAt: '',
            logoUrl: '',
            categories: [],
          };
          return (
            <Grid key={pin.connectorKey} size={{ xs: 6, sm: 4, md: 3 }} sx={{ display: 'flex' }}>
              <ConnectionCard
                variant="toolkit"
                toolkit={toolkit}
                showToolkitStats={catalog !== undefined}
                onSelect={() => onConnectComposio(pin.connectorKey)}
              />
            </Grid>
          );
        })}
      </Grid>
      <StyledDivider />
    </Box>
  );
}
